import { useEnv } from '@btcdirect/shared/src/composables/util/env';
import { useLocale } from '@btcdirect/shared/src/composables/util/locale';
import { useLogger } from '@btcdirect/shared/src/composables/util/logger';
import { ref, watch } from 'vue';
import { I18n } from 'vue-i18n';

const { debug, warn } = useLogger();
const { language: localeLanguage } = useLocale();

const i18nInstance = ref<I18n>();
const loadedDomainTranslations = new Map<string, Set<string>>();
const domains = new Set<string>();

const env = useEnv();

// TODO: Let histoire set the sessionStorage as well so that this env var is no longer needed
const testMode = env.getBoolean('VITE_TEST_MODE', false) || window.sessionStorage.getItem('TRANSLATION_TEST_MODE') === 'true';

// TODO: Check if there is a better place to put this
watch(localeLanguage, async () => {
    useTranslations().switchTranslationLanguage(localeLanguage.value);
});

export const useTranslations = (i18n?: I18n) => {
    // The i18n instance can only be registered once
    if (!!i18n && !i18nInstance.value) {
        i18nInstance.value = i18n;
    }

    /**
     * Register the domain for automatic translation retrieval
     */
    const addTranslationDomain = async (domain: string): Promise<void> => {
        if (!domain || domains.has(domain)) {
            return;
        }

        domains.add(domain);
        await getTranslations(domain, localeLanguage.value);
    };

    /**
     * Get the translations for the registered domains and switch to the language
     */
    const switchTranslationLanguage = async (language: string): Promise<void> => {
        for (const domain of domains) {
            await getTranslations(domain, language);
        }

        setTranslationLanguage(language);
    };

    /**
     * Get the translations file for the given domain in the current language
     */
    async function getTranslations(domain: string, language: string): Promise<void> {
        if (!domain) {
            return;
        }

        const foundDomain = loadedDomainTranslations.get(domain);

        // The translations for this domain and language already exist
        if (!!foundDomain) {
            if (foundDomain.has(language)) {
                return;
            }

            // The domain exists, but the language has not been loaded yet
            await retrieveTranslations(domain, language);
            foundDomain.add(language);

            return;
        }

        // The domain has not been loaded before
        loadedDomainTranslations.set(domain, new Set([language]));
        await retrieveTranslations(domain, language);
    }

    /**
     * Set the language for the translator
     */
    function setTranslationLanguage(language: string): void {
        i18nInstance.value.global.locale = language;

        document.querySelector('html').setAttribute('lang', language);
    }

    /**
     * Get the json file from the server
     */
    async function retrieveTranslations(domain: string, language: string): Promise<void> {
        try {
            const url = env.getString('VITE_ASSET_URL', '/');

            const response = await fetch(`${url}translations/${domain}/${language}.json`);
            const messages = await response.json();

            i18nInstance.value.global.mergeLocaleMessage(language, messages);

            debug('Successfully retrieved translations', `Domain: ${domain}`, `Language: ${language}`);
        } catch (exception) {
            warn('Could not retrieve translations', `Domain: ${domain}`, `Language: ${language}`);
        }
    }

    return {
        addTranslationDomain,
        switchTranslationLanguage,
        t: testMode ? (key: string, options?: any) => key : i18nInstance.value.global.t
    };
};
